/**
 * Accounts service — the client surface for admin-only account provisioning and
 * the public login helpers. Everything runs through two callable Cloud Functions
 * (`adminAccounts`, `accountLogin`) because creating/disabling auth users and
 * reading the `usernames` map require the Admin SDK — the browser can't.
 *
 *   Admin (must be signed in as admin):
 *     list()                    → [{ uid, email, username, role, disabled, passwordSource, passwordChangedAt, lastSignInTime, ... }]
 *     create(fields)            → { uid, email, username }   (fields include the admin-chosen password)
 *     setPassword(uid, password)→ { uid }   sets a real password; revokes existing sessions
 *     update(uid, patch)        → { uid }   patch: { role?, username?, disabled? }
 *     remove(uid)               → { uid }
 *
 * There are no temporary passwords: the admin picks the password and it stays
 * valid until someone changes it. Passwords can be SET but never READ — Firebase
 * Auth keeps only a salted hash, and a self-service reset completes on Google's
 * servers, so `list()` reports who last set the password and when, never the
 * value.
 *
 *   Public (login page, unauthenticated):
 *     resolveIdentifier(idOrUsername) → { email }   (username → email for sign-in)
 *     sendReset(idOrUsername)         → { ok: true } (branded reset email; never reveals existence)
 */
import { httpsCallable } from 'firebase/functions';

import { functions } from './config';

/** MACHINE_CODE → user-facing message. */
const ERROR_MESSAGES = {
  AUTH_REQUIRED: 'Please sign in and try again.',
  ADMIN_ONLY: 'Only administrators can manage accounts.',
  INVALID_EMAIL: 'Enter a valid email address.',
  INVALID_ROLE: 'Choose a valid role.',
  INVALID_USERNAME: 'Usernames are 3–30 characters: letters, numbers, dots or underscores.',
  USERNAME_TAKEN: 'That username is already taken.',
  EMAIL_IN_USE: 'An account already exists for that email.',
  ACCOUNT_NOT_FOUND: 'No account found for that ID.',
  CANNOT_REMOVE_SELF: "You can't delete your own account.",
  CANNOT_DISABLE_SELF: "You can't disable your own account.",
  UID_REQUIRED: 'Missing account reference — refresh and try again.',
  UNKNOWN_ACTION: 'Unsupported action.',
  PASSWORD_REQUIRED: 'Enter a password for this account.',
  WEAK_PASSWORD: 'Passwords must be 8–128 characters.',
  // Rate limits on the public callable. Both carry `details.retryAfterSeconds`;
  // callers that want to name the wait use `throttleMessage` below, and these
  // stay as the sensible fallback.
  RESET_THROTTLED: 'A reset link was already sent. Check your inbox, then try again shortly.',
  TOO_MANY_ATTEMPTS: 'Too many attempts from this network. Please wait a moment and try again.',
};

function friendly(err) {
  const machineCode = err?.message;
  const out = new Error(ERROR_MESSAGES[machineCode] || machineCode || 'Something went wrong — please try again.');
  out.code = err?.code;
  out.machineCode = machineCode;
  out.details = err?.details;
  return out;
}

/** Round a wait in seconds to the nearest sensible unit for user-facing copy. */
function formatWait(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 60) return 'a minute';
  const minutes = Math.ceil(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.ceil(minutes / 60);
  return `${hours} hour${hours === 1 ? '' : 's'}`;
}

/**
 * A rate-limit error's message with the actual wait named, or null if `err`
 * isn't a throttle. Telling someone "try again in 12 minutes" is the difference
 * between a limit that reads as a fault and one that reads as a rule.
 */
export function throttleMessage(err) {
  const code = err?.machineCode;
  if (code !== 'RESET_THROTTLED' && code !== 'TOO_MANY_ATTEMPTS') return null;
  const wait = formatWait(Number(err?.details?.retryAfterSeconds));
  return code === 'RESET_THROTTLED'
    ? `A reset link was already sent. Check your inbox, or try again in ${wait}.`
    : `Too many attempts from this network. Please try again in ${wait}.`;
}

const adminAccountsFn = httpsCallable(functions, 'adminAccounts');
const accountLoginFn = httpsCallable(functions, 'accountLogin');

async function callAdmin(action, payload = {}) {
  try {
    const res = await adminAccountsFn({ action, ...payload });
    return res.data;
  } catch (err) {
    throw friendly(err);
  }
}

async function callLogin(action, payload = {}) {
  try {
    const res = await accountLoginFn({ action, ...payload });
    return res.data;
  } catch (err) {
    throw friendly(err);
  }
}

export const accountsService = {
  // ---- admin ----
  list: () => callAdmin('list'),
  create: ({ email, username, name, role, password }) =>
    callAdmin('create', { email, username, name, role, password }),
  setPassword: (uid, password) => callAdmin('setPassword', { uid, password }),
  update: (uid, patch) => callAdmin('update', { uid, ...patch }),
  remove: (uid) => callAdmin('remove', { uid }),

  // ---- public login helpers ----
  resolveIdentifier: (identifier) => callLogin('resolve', { identifier }),
  sendReset: (identifier) => callLogin('sendReset', { identifier }),
};

export default accountsService;
